import {ActionIcon, Code, createStyles, Modal, Tooltip} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/analytics-products/ModelHubAnalyticsConstants';
import {Section} from 'constants/NavConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import navigateToURL from 'lib/navigateToURL';
import {PREDICTION_JOB_RESULTS_DOWNLOAD_URL_QUERY} from 'model-hub/ModelHubQueries';
import {navigateToProteinStructureTab} from 'molbio/aa-sequences/alphafold/AlphaFoldTabUtils';
import {AVAILABLE_PAGE_SIZES, DEFAULT_PAGE_SIZE} from 'nav/sidepanel/PageSizeUtil';
import {expandSection} from 'nav/sidepanel/SidepanelActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import SortableHeaderCell, {
  SORT_ICON_DOWN,
  SORT_ICON_UP,
  SortIcon,
} from 'ui-library/simple-table/SortableHeaderCell';
import APIUtil from 'util/APIUtil';
import {readableTime} from 'util/DateUtil';

import PredictionBatchStatusBadge from '../runs/PredictionBatchStatusBadge';
import type {SortDirection, SortField} from '../runs/PredictionBatchTypes';
import PredictionJobFilters from './PredictionJobFilters';
import {
  hasPredictionJobActiveFilters,
  readPredictionJobFiltersFromUrl,
  readPredictionJobSortFromUrl,
  writePredictionJobFiltersToUrl,
} from './predictionJobUrlParams';
import ResultsPreviewCell from './ResultsPreviewCell';
import type {PredictionJobSearchFilters} from './usePredictionJobSearch';
import {DEFAULT_FILTERS, usePredictionJobSearch} from './usePredictionJobSearch';

export interface ModelHubPredictionJobsTabProps {
  initialPredictionBatchId?: string | null;
  initialPredictionBatchLabel?: string | null;
  onNavigateToBatch: (batchId: string) => void;
}

export default function ModelHubPredictionJobsTab({
  initialPredictionBatchId,
  initialPredictionBatchLabel,
  onNavigateToBatch,
}: ModelHubPredictionJobsTabProps): JSX.Element {
  const [filters, setFilters] = useState<PredictionJobSearchFilters>(() => {
    const urlFilters = readPredictionJobFiltersFromUrl();
    if (initialPredictionBatchId && !urlFilters.predictionBatchId) {
      return {...urlFilters, predictionBatchId: initialPredictionBatchId};
    }
    return urlFilters;
  });
  const [batchLabel, setBatchLabel] = useState<string | null>(initialPredictionBatchLabel ?? null);
  const [sortField, setSortField] = useState<SortField>(() => readPredictionJobSortFromUrl().key);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    () => readPredictionJobSortFromUrl().direction
  );
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);

  const sort = useMemo(() => ({key: sortField, direction: sortDirection}), [sortField, sortDirection]);

  const {jobs, pagination, isLoading, error, refresh} = usePredictionJobSearch(
    filters,
    sort,
    offset,
    pageSize
  );

  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.PREDICTION_JOBS_TAB_VIEWED);
  }, []);

  // Derive batch label from search results when restored from URL without a label.
  // Use the batch name from the first loaded job rather than the raw API ID.
  useEffect(() => {
    if (filters.predictionBatchId && !batchLabel && jobs.length > 0) {
      const name = jobs[0].predictionBatchName;
      if (name) {
        setBatchLabel(name);
      }
    }
  }, [filters.predictionBatchId, batchLabel, jobs]);

  useEffect(() => {
    writePredictionJobFiltersToUrl(filters, sort);
  }, [filters, sort]);

  useEffect(() => {
    if (initialPredictionBatchId) {
      setFilters((prev) => {
        if (initialPredictionBatchId === prev.predictionBatchId) {
          return prev;
        }
        return {...prev, predictionBatchId: initialPredictionBatchId};
      });
      setBatchLabel(initialPredictionBatchLabel ?? null);
      setOffset(0);
    }
  }, [initialPredictionBatchId, initialPredictionBatchLabel]);

  const handleFiltersChange = useCallback((newFilters: PredictionJobSearchFilters) => {
    setFilters(newFilters);
    setOffset(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setBatchLabel(null);
    setOffset(0);
  }, []);

  const handleClearBatchFilter = useCallback(() => {
    setFilters((prev) => ({...prev, predictionBatchId: null}));
    setBatchLabel(null);
    setOffset(0);
  }, []);

  const handleSortClick = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDirection((prev: SortDirection) => (prev === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortField(field);
        setSortDirection('desc');
      }
      setOffset(0);
    },
    [sortField]
  );

  const handleSetOffset = useCallback((newOffset: number) => {
    setOffset(newOffset);
  }, []);

  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setOffset(0);
  }, []);

  const getSortIcon = (field: SortField): SortIcon =>
    field === sortField ? (sortDirection === 'desc' ? SORT_ICON_DOWN : SORT_ICON_UP) : null;

  const filtersActive = hasPredictionJobActiveFilters(filters);
  const isEmptyAndLoaded = jobs.length === 0 && !isLoading;

  let content: JSX.Element;
  if (error != null) {
    content = (
      <div className={css(styles.emptyState)}>
        <div className={css(styles.emptyStateTitle)}>Something went wrong</div>
        <div className={css(styles.emptyStateSubtext)}>
          We couldn&apos;t load prediction jobs. Please try again.
        </div>
        <Button buttonStyle={ButtonStyle.SECONDARY} size={ButtonSize.MEDIUM} text="Retry" onClick={refresh} />
      </div>
    );
  } else if (isEmptyAndLoaded && !filtersActive) {
    content = (
      <div className={css(styles.emptyState)}>
        <div className={css(styles.emptyStateTitle)}>No prediction jobs yet</div>
        <div className={css(styles.emptyStateSubtext)}>
          Prediction jobs will appear here when models are run.
        </div>
      </div>
    );
  } else if (isEmptyAndLoaded && filtersActive) {
    content = (
      <div className={css(styles.emptyState)}>
        <div className={css(styles.emptyStateTitle)}>No prediction jobs match your filters</div>
        <div className={css(styles.emptyStateSubtext)}>Try adjusting your filters or clearing them.</div>
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          text="Clear filters"
          onClick={handleClearFilters}
        />
      </div>
    );
  } else {
    content = (
      <Table
        numRows={jobs.length}
        isLoading={isLoading}
        pagination={pagination}
        setOffset={handleSetOffset}
        getRowKey={(rowIndex: number) => jobs[rowIndex].id}
        calculateInView={BenchlingConfig.MODEL_HUB_ENABLE_PREDICTION_JOB_PREVIEW_COLUMN}
      >
        <Column
          header={<Cell>Components</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell>
              <ComponentChips componentApiIds={jobs[rowIndex].componentApiIds} />
            </Cell>
          )}
        />
        <Column
          header={<Cell>Status</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell>
              <PredictionBatchStatusBadge status={jobs[rowIndex].status} />
            </Cell>
          )}
        />
        <Column
          header={<Cell>Prediction Batch</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell>
              <button
                type="button"
                className={css(styles.batchLink)}
                onClick={() => {
                  onNavigateToBatch(jobs[rowIndex].predictionBatchId);
                }}
                {...getTestProps({component: 'ModelHubPredictionJobsTab', element: 'batchName'})}
              >
                {jobs[rowIndex].predictionBatchName}
              </button>
            </Cell>
          )}
        />
        <Column
          header={<Cell>Model</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell>
              <span {...getTestProps({component: 'ModelHubPredictionJobsTab', element: 'modelName'})}>
                {jobs[rowIndex].modelName}
              </span>
            </Cell>
          )}
        />
        <Column
          header={
            <SortableHeaderCell
              sortIcon={getSortIcon('createdAt')}
              onToggleSort={() => {
                handleSortClick('createdAt');
              }}
            >
              Created
            </SortableHeaderCell>
          }
          renderCell={(rowIndex: number) => <Cell>{readableTime(jobs[rowIndex].createdAt)}</Cell>}
        />
        <Column
          header={
            <SortableHeaderCell
              sortIcon={getSortIcon('modifiedAt')}
              onToggleSort={() => {
                handleSortClick('modifiedAt');
              }}
            >
              Modified
            </SortableHeaderCell>
          }
          renderCell={(rowIndex: number) => <Cell>{readableTime(jobs[rowIndex].modifiedAt)}</Cell>}
        />
        {BenchlingConfig.MODEL_HUB_ENABLE_PREDICTION_JOB_PREVIEW_COLUMN ? (
          <Column
            header={<Cell>Preview</Cell>}
            renderCell={(rowIndex: number) => (
              <Cell>
                <ResultsPreviewCell
                  standardizedOutputDownloadUrl={jobs[rowIndex].standardizedOutputDownloadUrl}
                  componentApiIds={jobs[rowIndex].componentApiIds}
                  onRefreshDownloadUrls={refresh}
                />
              </Cell>
            )}
          />
        ) : null}
        <Column
          header={<Cell>Results</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell>
              {jobs[rowIndex].status === 'SUCCEEDED' && jobs[rowIndex].hasResultsFile ? (
                <DownloadResultsButton jobId={jobs[rowIndex].id} />
              ) : (
                <span className={css(styles.noComponents)}>—</span>
              )}
            </Cell>
          )}
        />
        <Column
          header={<Cell>Config</Cell>}
          renderCell={(rowIndex: number) => (
            <Cell>
              {Object.keys(jobs[rowIndex].configJson).length > 0 ? (
                <ViewConfigButton configJson={jobs[rowIndex].configJson} jobId={jobs[rowIndex].id} />
              ) : (
                <span className={css(styles.noComponents)}>—</span>
              )}
            </Cell>
          )}
        />
      </Table>
    );
  }

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'ModelHubPredictionJobsTab', element: 'root'})}
    >
      <div className={css(styles.toolbarRow)}>
        <PredictionJobFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          predictionBatchLabel={batchLabel}
          onClearBatchFilter={handleClearBatchFilter}
        />
        <label className={css(styles.pageSizeLabel)}>
          Rows per page:{' '}
          <select value={pageSize} onChange={handlePageSizeChange}>
            {AVAILABLE_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
      {content}
    </div>
  );
}

interface PredictionJobResultsDownloadUrlQuery {
  predictionJob: {
    id: string;
    resultsDownloadUrl: string | null;
  } | null;
}

function DownloadResultsButton({jobId}: {jobId: string}): JSX.Element {
  const [requesting, setRequesting] = useState(false);

  const onClick = async (): Promise<void> => {
    setRequesting(true);
    try {
      const result = await doQuerySafe<PredictionJobResultsDownloadUrlQuery>(
        {query: PREDICTION_JOB_RESULTS_DOWNLOAD_URL_QUERY},
        {
          variables: {id: jobId},
          fetchPolicy: 'no-cache',
        }
      );
      const url = result.predictionJob?.resultsDownloadUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Tooltip label="Download results" withinPortal={true}>
      <ActionIcon
        aria-label={`Download results for ${jobId}`}
        variant="tertiary"
        onClick={onClick}
        disabled={requesting}
      >
        <BenchlingIcon icon="download" />
      </ActionIcon>
    </Tooltip>
  );
}

function ViewConfigButton({
  configJson,
  jobId,
}: {
  configJson: Record<string, unknown>;
  jobId: string;
}): JSX.Element {
  const [opened, {open, close}] = useDisclosure(false);
  const {classes} = useConfigModalStyles();
  const formattedJson = JSON.stringify(configJson, null, 2);

  return (
    <>
      <Tooltip label="View config" withinPortal={true}>
        <ActionIcon
          aria-label={`View config for ${jobId}`}
          variant="tertiary"
          onClick={open}
          {...getTestProps({component: 'ModelHubPredictionJobsTab', element: 'viewConfigButton'})}
        >
          <BenchlingIcon icon="info" />
        </ActionIcon>
      </Tooltip>
      <Modal opened={opened} onClose={close} title="Prediction Job Config" size="lg">
        <Code className={classes.codeBlock}>{formattedJson}</Code>
      </Modal>
    </>
  );
}

function ComponentChips({componentApiIds}: {componentApiIds: ReadonlyArray<string>}): JSX.Element {
  if (componentApiIds.length === 0) {
    return <span className={css(styles.noComponents)}>—</span>;
  }
  return (
    <div className={css(styles.chipContainer)}>
      {componentApiIds.map((apiId) => (
        <BenchChip
          key={apiId}
          id={apiId}
          size={ChipSize.SMALL}
          onClick={(e: {stopPropagation: () => void}, item: {editUrl?: string}) => {
            e.stopPropagation();
            if (APIUtil.isProtein(apiId)) {
              navigateToProteinStructureTab(apiId).catch(() => {});
            } else {
              expandSection(Section.PROJECTS);
              if (item.editUrl) {
                navigateToURL(item.editUrl);
              }
            }
          }}
        />
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  pageSizeLabel: {
    fontSize: 13,
    color: Colors.GRAY_DARKER,
    flexShrink: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 24px',
    textAlign: 'center',
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: Colors.NEAR_BLACK,
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: Colors.GRAY_DARKER,
    marginBottom: 16,
  },
  batchLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: Colors.BLUE,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
  },
  noComponents: {
    color: Colors.GRAY_DARKER,
  },
});

const useConfigModalStyles = createStyles((theme) => ({
  codeBlock: {
    display: 'block',
    whiteSpace: 'pre',
    overflow: 'auto',
    maxHeight: 480,
    fontSize: theme.fontSizes.sm,
  },
}));
